import { useEffect, useState, useRef } from "react";
import { Chat } from "@launch10/use-langgraph-stream";
import {
  RECOMMENDATION_PANELS,
  GenericSubmission, recommendationIconFor
} from "@components/chat/RecommendationPanels";
import { recommendationsForSurface } from "@lib/recommendationsRegistry";
import { useBrainstormSelector } from "@components/brainstorm/hooks";
import { BrainstormMessages } from "./chat/BrainstormMessages";
import { BrainstormInput } from "../shared/BrainstormInput";
import { BrainstormChatSkeleton } from "./chat/BrainstormChatSkeleton";
import { PaginationFooter } from "@components/shared/pagination-footer";
import { FirstBrainstormNotification } from "@components/app-notifications";

const SKELETON_DELAY_MS = 200;

/**
 * Inner component for brainstorm conversation content.
 * Chat.Root is provided by parent Brainstorm component.
 */
function BrainstormConversationContent({
  contentVisible,
}: {
  contentVisible: boolean;
}) {
  // Brainstorm is complete once every topic is answered or skipped — the graph
  // streams an empty remainingTopics. Gate on the array being present-and-empty
  // (not on currentTopic being falsy) so the button stays disabled while state
  // is still loading.
  const remainingTopics = useBrainstormSelector((s) => s.values?.remainingTopics);
  const canContinue = Array.isArray(remainingTopics) && remainingTopics.length === 0;

  return (
    <div
      className={`h-full flex flex-col transition-opacity duration-300 ease-out ${contentVisible ? "opacity-100" : "opacity-0"
        }`}
    >
      <div className="flex-1 min-h-0 overflow-hidden mx-auto container max-w-3xl grid grid-rows-[1fr] px-8">
        {/* Main chat area — recommendations are part of the chat widget:
            the chips row sits above the input, and an active recommendation
            transforms the composer into its panel. */}
        <Chat.Recommendations.Root
          renderers={RECOMMENDATION_PANELS}
          defaultSubmission={GenericSubmission}
          recommendations={recommendationsForSurface("brainstorm")}
          iconFor={recommendationIconFor}
          notePlaceholder="Add a note for Houston… (optional)"
        >
          <div className="flex flex-col min-h-0 overflow-hidden">
            <BrainstormMessages />
            {/* Fixed bottom input area */}
            <div className="shrink-0 bg-neutral-background">
              {/* Same width as the Composer below — chips align flush with the chat box edges */}
              <div className="max-w-3xl mx-auto">
                <Chat.Recommendations.Chips label="Suggested next steps" className="pb-3" />
              </div>
              <Chat.Recommendations.Composer className="max-w-3xl mx-auto mb-8">
                <BrainstormInput maxRows={12} />
              </Chat.Recommendations.Composer>
            </div>
          </div>
        </Chat.Recommendations.Root>
      </div>

      {/* Pagination Footer */}
      <PaginationFooter.Root layout="container" canGoBack={false} canGoForward={canContinue}>
        <div /> {/* Empty div for left side since no back button */}
        <PaginationFooter.Actions>
          <PaginationFooter.ContinueButton disabled={!canContinue}>
            {canContinue ? "Build My Site" : "Continue"}
          </PaginationFooter.ContinueButton>
        </PaginationFooter.Actions>
      </PaginationFooter.Root>
    </div>
  );
}

/**
 * View for existing brainstorm conversations.
 * Uses delayed skeleton + fade transitions for smooth loading UX.
 *
 * Only shows the loading state on initial mount. After content has been
 * shown once, subsequent isThreadLoading flips (e.g. the SDK refetching
 * history after a stream completes) are ignored to prevent a visible blink.
 */
export function BrainstormConversationPage() {
  const isLoadingHistory = useBrainstormSelector((s) => s.isThreadLoading);
  const isStreaming = useBrainstormSelector((s) => s.isLoading);
  const hasMessages = useBrainstormSelector((s) => s.messages.length > 0);

  // Only treat as "loading" on initial mount before we have any content.
  // Once content has been shown, ignore subsequent history refetch flickers.
  const hasShownContentRef = useRef(false);
  if (!isLoadingHistory || hasMessages) {
    hasShownContentRef.current = true;
  }
  const isLoading = isLoadingHistory && !hasShownContentRef.current;

  const [showSkeleton, setShowSkeleton] = useState(false);
  const skeletonTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [contentVisible, setContentVisible] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  // Show notification 800ms after streaming finishes — gives user a beat to read the response
  useEffect(() => {
    if (!contentVisible || isStreaming) {
      setShowNotification(false);
      return;
    }
    setShowNotification(true);
  }, [contentVisible, isStreaming]);

  useEffect(() => {
    let rafId: number | undefined;

    if (isLoading) {
      skeletonTimerRef.current = setTimeout(() => {
        setShowSkeleton(true);
      }, SKELETON_DELAY_MS);
      setContentVisible(false);
    } else {
      if (skeletonTimerRef.current) {
        clearTimeout(skeletonTimerRef.current);
        skeletonTimerRef.current = null;
      }
      setShowSkeleton(false);
      rafId = requestAnimationFrame(() => {
        setContentVisible(true);
      });
    }

    return () => {
      if (skeletonTimerRef.current) {
        clearTimeout(skeletonTimerRef.current);
      }
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isLoading]);

  if (isLoading && showSkeleton) {
    return (
      <div className="transition-opacity duration-200 ease-out opacity-100">
        <BrainstormChatSkeleton />
      </div>
    );
  }

  if (isLoading) {
    return <div className="h-full" />;
  }

  return (
    <>
      {showNotification && <FirstBrainstormNotification />}
      <BrainstormConversationContent contentVisible={contentVisible} />
    </>
  );
}
